/**
 * HH Finance Module - Data Hooks
 *
 * React hooks for fetching and managing finance data
 */

import { useState, useEffect, useCallback } from 'react';
import { hhFinanceApi } from '../services';
import type {
  DashboardSummary,
  HealthStatus,
  BankAccount,
  InvoiceSummary,
  BillSummary,
  SchedulerStatus,
  ScheduledJob,
} from '../types';

// Generic async state hook
interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function useAsync<T>(fetchFn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

/**
 * Hook: API Health Status
 */
export function useHealthStatus(): AsyncState<HealthStatus> {
  return useAsync(() => hhFinanceApi.getHealth(), []);
}

/**
 * Hook: Full Dashboard Summary
 */
export function useDashboardSummary(): AsyncState<DashboardSummary> {
  return useAsync(() => hhFinanceApi.getDashboardSummary(), []);
}

/**
 * Hook: Bank Accounts
 */
export function useBankAccounts(): AsyncState<BankAccount[]> {
  return useAsync(async () => {
    const response = await hhFinanceApi.getBankAccounts();
    return response.accounts;
  }, []);
}

/**
 * Hook: Open Invoices
 */
export function useOpenInvoices(): AsyncState<InvoiceSummary[]> {
  return useAsync(async () => {
    const response = await hhFinanceApi.getOpenInvoices();
    return response.invoices;
  }, []);
}

/**
 * Hook: Overdue Invoices
 */
export function useOverdueInvoices(): AsyncState<InvoiceSummary[]> {
  return useAsync(async () => {
    const response = await hhFinanceApi.getOverdueInvoices();
    return response.invoices;
  }, []);
}

/**
 * Hook: Open Bills
 */
export function useOpenBills(): AsyncState<BillSummary[]> {
  return useAsync(async () => {
    const response = await hhFinanceApi.getOpenBills();
    return response.bills;
  }, []);
}

/**
 * Hook: Bills Due This Week
 */
export function useBillsDueThisWeek(): AsyncState<BillSummary[]> {
  return useAsync(async () => {
    const response = await hhFinanceApi.getBillsDueThisWeek();
    return response.bills;
  }, []);
}

/**
 * Hook: Scheduler Status
 */
export function useSchedulerStatus(): AsyncState<SchedulerStatus> {
  return useAsync(() => hhFinanceApi.getSchedulerStatus(), []);
}

/**
 * Hook: Scheduled Jobs
 */
export function useScheduledJobs(): AsyncState<ScheduledJob[]> {
  return useAsync(async () => {
    const response = await hhFinanceApi.getScheduledJobs();
    return response.jobs;
  }, []);
}

/**
 * Hook: Combined Finance Dashboard
 * Fetches all data needed for the main dashboard view
 */
export interface FinanceDashboardData {
  summary: DashboardSummary | null;
  scheduler: SchedulerStatus | null;
  jobs: ScheduledJob[];
}

export function useFinanceDashboard(): AsyncState<FinanceDashboardData> {
  return useAsync(async () => {
    const [summary, scheduler, jobsResponse] = await Promise.all([
      hhFinanceApi.getDashboardSummary(),
      hhFinanceApi.getSchedulerStatus(),
      hhFinanceApi.getScheduledJobs(),
    ]);
    return {
      summary,
      scheduler,
      jobs: jobsResponse.jobs,
    };
  }, []);
}
